import type { Availability } from '@workflow/contracts'
import { cn, StatusLabel, StatusMark, type StatusTone } from '@workflow/ui'
import { useSession, useSetAvailability } from '@/lib/api/hooks'

const CLOCK_STATE: Record<Availability, { tone: StatusTone; label: string }> = {
  active: { tone: 'clocked-in', label: 'Clocked In' },
  inactive: { tone: 'clocked-out', label: 'Clocked Out' },
}

/** The same availability choices in the portal and the last onboarding step. */
export function AvailabilityPanel({ current, onChange, pending = false, error, onboarding = false }: {
  current: Availability
  onChange: (availability: Availability) => void
  pending?: boolean
  error?: string
  onboarding?: boolean
}) {
  return (
    <section className={cn(!onboarding && 'rounded-2xl border border-hairline bg-surface p-5 shadow-chip')} aria-busy={pending || undefined}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-ui font-medium">{onboarding ? 'Choose your availability' : 'Your availability'}</h3>
        <StatusLabel tone={CLOCK_STATE[current].tone} label={CLOCK_STATE[current].label} />
      </div>
      <p className="mt-2 text-ui leading-relaxed text-muted-foreground">
        {onboarding
          ? 'Tell the studio whether you are ready for new tasks. New accounts start clocked out. You can change this from your portal after approval.'
          : 'Clock in when you are ready for new tasks. Clock out when you are unavailable; your existing work stays with you.'}
      </p>
      <ClockButtons current={current} onChange={onChange} pending={pending} className="mt-4" />
      <p className="mt-4 text-caption leading-relaxed text-muted-foreground" aria-live="polite">
        {pending ? 'Saving your availability...' : current === 'active'
          ? onboarding ? 'You are clocked in. The studio can assign new work once your account is approved.' : 'You are clocked in. The studio can assign new work to you.'
          : 'You are clocked out. No new work is assigned; work you already hold is unaffected.'}
        {onboarding ? ' This setting is saved when you select it. It does not track work hours or approve your profile.' : ''}
      </p>
      {error ? <p role="alert" className="mt-2 text-caption text-danger">{error}</p> : null}
    </section>
  )
}

/**
 * Two small options, not two cards: a lit dot and a name each, the chosen
 * one inked. The dot is the whole explanation — green takes new work, red
 * does not — so the control carries no sentences of its own.
 */
function ClockButtons({ current, onChange, pending, className }: {
  current: Availability
  onChange: (availability: Availability) => void
  pending: boolean
  className?: string
}) {
  return (
    <div className={cn('flex items-center gap-1', className)} role="group" aria-label="Availability" aria-busy={pending || undefined}>
      {(['active', 'inactive'] as const).map((availability) => {
        const selected = current === availability
        const clockIn = availability === 'active'
        return (
          <button key={availability} type="button" aria-label={clockIn ? 'Clock In' : 'Clock Out'}
            aria-pressed={selected} disabled={selected || pending}
            onClick={() => onChange(availability)}
            className={cn(
              'flex items-center gap-2 rounded-full px-3 py-1.5 text-ui transition-colors duration-fast outline-none',
              'focus-visible:ring-1 focus-visible:ring-foreground disabled:cursor-default',
              selected ? 'bg-selected text-foreground' : 'text-muted-foreground hover:bg-tint hover:text-foreground',
              pending && 'cursor-wait opacity-60',
            )}>
            <StatusMark tone={clockIn ? 'clocked-in' : 'clocked-out'} />
            {clockIn ? 'Clock In' : 'Clock Out'}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The player's clock, the first thing on their Home: the pair alone at the
 * page's top right, no holder around it — the chosen one is simply lit.
 * A refusal speaks through the toast, as every action's does.
 */
export function AvailabilityCard() {
  const session = useSession()
  const me = session.data?.account ?? null
  const move = useSetAvailability()
  if (!me || me.role !== 'player') return null
  return (
    <ClockButtons
      current={me.availability}
      pending={move.isPending}
      onChange={(availability) => move.mutate({ availability })}
    />
  )
}
